'use client'

import Link from 'next/link'
import styled from 'styled-components'

import type { SectionListItemDto } from '../../shared/api/generated'
import { colors } from '../../shared/constants/colors'
import { getSectionImage } from '../../shared/constants/sectionImages'
import { SectionItem } from '../../shared/ui-kit/actual/SectionItem'
import { MainHeader } from '../../shared/ui-kit/actual/headers/MainHeader'
import {
  breakpoints,
  devices,
  mobileView,
  tabletDesktopView,
} from '../../shared/ui-kit/breakPoints'

interface SectionsServerViewProps {
  sections: SectionListItemDto[]
}

const sectionDescriptions: Record<string, string> = {
  'Водный транспорт и авиация': 'Техника',
  'Здоровье и медицина': 'Для дома и дачи',
  'Спец. техника': 'Техника',
  Спорт: 'Спорт.инвентарь',
  Другое: 'Различные товары',
}

const sectionOrder = [
  'Инструменты',
  'Автомобили',
  'Одежда',
  'Недвижимость',
  'Цифровая техника',
  'Спец. техника',
  'Техника',
  'Водный транспорт и авиация',
  'Люди',
  'Животные',
  'Для дома и дачи',
  'Здоровье и медицина',
  'Аксессуары',
  'Для бизнеса',
  'Творчество',
  'Товары для детей',
  'Путешествия',
  'Спорт',
  'Для фотосессий',
  'Развлечения',
  'Другое',
]

const sectionOrderMap = new Map(sectionOrder.map((name, index) => [name, index]))

export function SectionsServerView({ sections }: SectionsServerViewProps) {
  const orderedSections = [...sections].sort((firstSection, secondSection) => {
    const firstPosition = sectionOrderMap.get(firstSection.name) ?? sectionOrder.length
    const secondPosition = sectionOrderMap.get(secondSection.name) ?? sectionOrder.length

    return firstPosition - secondPosition
  })

  return (
    <BasePage>
      <MainHeader />
      <TopBlock>
        <ArrowImage href={'/'}>
          <img alt={'arrow-back'} src={'/images/buttons/arrow-right-black.svg'} />
        </ArrowImage>
        <FlexRow style={{ flexGrow: 1 }}>
          <H1>Разделы</H1>
        </FlexRow>
      </TopBlock>
      <BaseWrapper>
        <BreadcrumbsWrapper>
          <Link href={'/'}>Главная</Link>
          <span>
            {' '}
            / <Link href={'/sections'}>Разделы</Link>
          </span>
        </BreadcrumbsWrapper>
        <SectionsPageWrapper>
          {orderedSections.map(section => (
            <SectionItem
              description={sectionDescriptions[section.name]}
              img={getSectionImage(section)}
              key={section.id}
              productCount={section.productsCount}
              title={section.name}
              to={`/sections/${section.id}`}
            />
          ))}
        </SectionsPageWrapper>
      </BaseWrapper>
      <BottomMenu aria-label={'Нижнее меню'}>
        <BottomMenuInner>
          <BottomItem href={'/orders'}>
            <img alt={''} src={'/images/bottom-menu/cart-gray.svg'} />
            <span>Заказы</span>
          </BottomItem>
          <BottomItem href={'/messages'}>
            <img alt={''} src={'/images/bottom-menu/chat-gray.svg'} />
            <span>Чат</span>
          </BottomItem>
          <BottomItem href={'/blog'}>
            <img alt={''} src={'/images/bottom-menu/blog-gray.svg'} />
            <span>Блог</span>
          </BottomItem>
          <BottomItem href={'/'}>
            <img alt={''} src={'/images/bottom-menu/main-gray.svg'} />
            <span>Главная</span>
          </BottomItem>
          <BottomItem href={'/profile'}>
            <img alt={''} src={'/images/bottom-menu/profile-gray.svg'} />
            <span>Профиль</span>
          </BottomItem>
        </BottomMenuInner>
      </BottomMenu>
    </BasePage>
  )
}

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`

const FlexColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const FlexRowBetween = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
`

const BasePage = styled(FlexColumn)`
  padding: 0 12px 60px;
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMax});
  margin: 0 auto;
  @media (${devices.tabletMin}) {
    padding: 0 36px 60px;
    overflow: auto;
  }
`

const TopBlock = styled(FlexRowBetween)`
  align-items: center;
  margin: 24px 0 12px;
  ${mobileView}
`

const ArrowImage = styled(Link)`
  width: 24px;
  height: 24px;
  display: block;
  cursor: pointer;
  img {
    width: 24px;
    height: 24px;
    transform: rotate(180deg);
  }
`

const H1 = styled.h1`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: center;
  color: #000000;
`

const BaseWrapper = styled.div`
  width: 500px;
  width: 100%;
  padding: 0 36px;
  @media screen and (width <= ${breakpoints.tabletMin}) {
    padding: 0;
  }
`

const BreadcrumbsWrapper = styled.div`
  font-size: 18px;
  color: #000;
  margin: 32px 0 24px;
  width: 100%;
  a {
    color: #000;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  ${tabletDesktopView}
`

const SectionsPageWrapper = styled(FlexRowBetween)`
  gap: 20px;
  width: 100%;
  margin-top: 0;
  overflow: auto;
  padding: 0 7px 10px;
  justify-content: center;
  flex-wrap: wrap;
  @media (min-width: ${breakpoints.mobileMax}) {
    gap: 40px;
  }
`

const BottomMenu = styled.nav`
  position: fixed;
  bottom: -1px;
  left: 0px;
  margin: 0;
  border-top: 1px solid ${colors.lightGray};
  background-color: white;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 30px 30px 0px 0px;
  z-index: 2000;
  box-shadow: 0px -5px 60px 0px rgba(0, 0, 0, 0.05);
  @media (min-width: ${breakpoints.desktopMin}) {
    display: none;
  }
`

const BottomMenuInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 19px 37px 20px 37px;
`

const BottomItem = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 40px;
  width: 40px;
  row-gap: 4px;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  img,
  svg {
    width: 20px;
    height: 20px;
  }

  span {
    color: ${colors.grey};
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
  }
`
